import Konva from 'konva';
import React, { useMemo, forwardRef } from 'react';
import { Layer, Stage, Rect } from 'react-konva';

import { useRestaurant } from '../../../../selectors/RestaurantSelectors';
import personIcon from '../../../shared/assets/icons/person.svg';
// import { useResponsiveFlags } from '../../../shared/lib';
import { useColoredSvgImage } from '../lib';

import { CanvasImage } from './canvasImage';
import { SchemaBasicElement } from './schemaBasicElement';
import { SchemaTable } from './schemaTable';

export const SchemaStage = forwardRef<
  Konva.Stage,
  {
    size: { width: number; height: number };
    contentSize: { width: number; height: number };
    scale: number;
    bounds: { minX: number; maxX: number; minY: number; maxY: number };
    position: { x: number; y: number };
    onSetPosition: (pos: { x: number; y: number }) => void;
    onDragStart?: () => void;
    onDragMove?: (e: Konva.KonvaEventObject<DragEvent>) => void;
    onDragEnd?: (e: Konva.KonvaEventObject<DragEvent>) => void;
    schema: GM.ReservesSchemas.SchemaDTO;
    selectedTableIds: number[];
    onClickSchema: (e: Konva.KonvaEventObject<MouseEvent>) => void;
    onClickTable: (id: number) => void;
    tablesSchedule: GM.ReservesSchemas.TablesSchedule;
  }
>(function SchemaStage(
  {
    size,
    contentSize,
    scale,
    bounds,
    schema,
    onSetPosition,
    position,
    selectedTableIds,
    onClickTable,
    tablesSchedule,
    onClickSchema,
    onDragStart,
    onDragMove,
    onDragEnd: onDragEndProp,
  },
  ref,
) {
  // const { isMobile } = useResponsiveFlags();
  // Отступ, чтобы не обрезались элементы столов, которые будут выступать за границы холста
  const offset = useMemo(() => {
    const overlapX = 18;
    const overlapY = 18;
    const offset: Konva.Vector2d = { x: 0, y: 0 };

    if (schema.tables.some((st) => st.x < overlapX)) {
      offset.x = -overlapX;
    }

    if (schema.tables.some((st) => st.y < overlapY)) {
      offset.y = -overlapY;
    }

    return offset;
  }, [schema.tables]);

  // const overEdges = useMemo(() => {
  //   const result = [];
  //
  //   schema.tables.forEach((st) => {
  //     if (st.x < 0) {
  //       result.push('left');
  //     }
  //     if (st.y < 0) {
  //       result.push('top');
  //     }
  //     if (st.x + st.width > contentSize.width) {
  //       result.push('right');
  //     }
  //     if (st.y + st.height > contentSize.height) {
  //       result.push('bottom');
  //     }
  //   });
  //
  //   return result;
  // }, [schema.tables]);

  const schemaElements = useMemo(() => {
    const elements: GM.ReservesSchemas.SchemaElements = [];

    schema.tables.forEach((ele: GM.ReservesSchemas.TableDTO) => {
      elements.push({
        ...ele,
        type: 'TABLE',
      });
    });

    schema.marks.forEach((ele: GM.ReservesSchemas.MarkDTO) => {
      elements.push({
        ...ele,
        type: 'MARK',
      });
    });

    schema.rectangles.forEach((ele: GM.ReservesSchemas.RectangleDTO) => {
      elements.push({
        ...ele,
        type: 'RECTANGLE',
      });
    });

    schema.ellipses.forEach((ele: GM.ReservesSchemas.EllipseDTO) => {
      elements.push({
        ...ele,
        type: 'ELLIPSE',
      });
    });

    // Приоритет для reserveTimeStatus (меньшее значение = раньше в массиве = ниже на canvas)
    const statusPriority: Record<GM.ReservesSchemas.TimeStatus, number> = {
      FREE: 1,
      OPEN: 2,
      ARRIVED: 3,
      SOON: 4,
      DUE: 5,
    };

    // Оптимизация: сортируем только если есть столы с расписанием
    // Для статических элементов (marks, rectangles, ellipses) сортировка не нужна
    const hasTablesWithSchedule = elements.some(
      (e) => e.type === 'TABLE' && tablesSchedule[e.table.id]?.attributes?.reserveTimeStr,
    );

    if (!hasTablesWithSchedule) {
      // Если нет столов с расписанием, просто сортируем по z
      return elements.sort((a, b) => a.z - b.z);
    }

    return elements.sort((a, b) => {
      const isTableA = a.type === 'TABLE';
      const isTableB = b.type === 'TABLE';

      // Получаем данные расписания для столов
      const scheduleA = isTableA && tablesSchedule[a.table.id];
      const scheduleB = isTableB && tablesSchedule[b.table.id];

      const hasScheduleA =
        scheduleA &&
        scheduleA.attributes?.reserveTimeStr &&
        scheduleA.attributes?.reserveTimeStatus;
      const hasScheduleB =
        scheduleB &&
        scheduleB.attributes?.reserveTimeStr &&
        scheduleB.attributes?.reserveTimeStatus;

      // Столы с расписанием всегда выше (в конце массива для canvas)
      if (hasScheduleA && !hasScheduleB) return 1;
      if (!hasScheduleA && hasScheduleB) return -1;

      // Оба элемента — столы с расписанием
      if (hasScheduleA && hasScheduleB && scheduleA.attributes && scheduleB.attributes) {
        const statusA = scheduleA.attributes.reserveTimeStatus;
        const statusB = scheduleB.attributes.reserveTimeStatus;

        if (statusA && statusB) {
          const priorityDiff = statusPriority[statusA] - statusPriority[statusB];

          if (priorityDiff !== 0) return priorityDiff;
        }

        // Сортировка по reserveTimeStr (обратная - более ранние выше)
        const timeA = scheduleA.attributes.reserveTimeStr;
        const timeB = scheduleB.attributes.reserveTimeStr;

        if (timeA && timeB && timeA !== timeB) {
          return timeA > timeB ? -1 : 1;
        }

        // Сортировка по z
        return a.z - b.z;
      }

      // Обычная сортировка по z для всех остальных элементов
      return a.z - b.z;
    });
  }, [schema.tables, schema.marks, schema.rectangles, schema.ellipses, tablesSchedule]);

  const selectedTables = useMemo<
    Array<GM.ReservesSchemas.SchemaTable | GM.ReservesSchemas.SchemaListTable>
  >(() => {
    if (schema.tables?.length) {
      return schema.tables
        .filter((st) => selectedTableIds.includes(st.table.id))
        .map((st) => ({ ...st, type: 'TABLE' as const }));
    }

    return [];
  }, [schema.tables, selectedTableIds]);

  const staticElements = useMemo<
    Array<
      | GM.ReservesSchemas.SchemaMark
      | GM.ReservesSchemas.SchemaRectangle
      | GM.ReservesSchemas.SchemaEllipse
    >
  >(
    () =>
      schemaElements.filter(
        (
          e,
        ): e is
          | GM.ReservesSchemas.SchemaMark
          | GM.ReservesSchemas.SchemaRectangle
          | GM.ReservesSchemas.SchemaEllipse => e.type !== 'TABLE' && e.type !== 'LIST_TABLE',
      ),
    [schemaElements],
  );

  const tableElements = useMemo<
    Array<GM.ReservesSchemas.SchemaTable | GM.ReservesSchemas.SchemaListTable>
  >(
    () =>
      schemaElements.filter(
        (e): e is GM.ReservesSchemas.SchemaTable | GM.ReservesSchemas.SchemaListTable =>
          (e.type === 'TABLE' || e.type === 'LIST_TABLE') && !selectedTableIds.includes(e.table.id),
      ),
    [schemaElements, selectedTableIds],
  );

  const isTableList = !(schema.tables.length > 0);
  // const [personImage] = useImage(personIcon);
  const [personImage] = useColoredSvgImage(personIcon, '#FFFFFF');

  const restaurant = useRestaurant();
  const restaurantTz = restaurant?.info?.timeZoneOffset || undefined;

  if (schemaElements.length === 0) {
    return null;
  }

  return (
    <Stage
      ref={ref}
      width={size.width}
      height={size.height}
      scaleX={scale}
      scaleY={scale}
      x={position.x}
      y={position.y}
      draggable
      dragBoundFunc={(pos) => {
        // Только вычисление ограничений, без синхронизации состояния
        // Синхронизация происходит в onDragEnd для оптимизации производительности
        return {
          x: Math.max(bounds.minX, Math.min(bounds.maxX, pos.x)),
          y: Math.max(bounds.minY, Math.min(bounds.maxY, pos.y)),
        };
      }}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={(e) => {
        // Синхронизация только в конце drag для оптимизации производительности
        const pos = e.target.position();
        onSetPosition({ x: pos.x, y: pos.y });
        // Вызываем внешний обработчик если он передан
        if (onDragEndProp) {
          onDragEndProp(e);
        }
      }}
      offset={offset}
      onClick={onClickSchema}
      onTap={onClickSchema}
    >
      {schema.images
        ?.filter((item) => item.image?.accessUrl)
        .map((item) => (
          <Layer key={item.id}>
            <CanvasImage
              src={item.image.accessUrl}
              width={item.width}
              height={item.height}
              x={item.x}
              y={item.y}
            />
          </Layer>
        ))}

      {/* Слой статики: фон (при isTableList) и декоративные элементы — не перерисовывается при изменении tablesSchedule */}
      {(isTableList || staticElements.length > 0) && (
        <Layer listening={false}>
          {isTableList && (
            <Rect
              x={0}
              y={0}
              width={contentSize.width}
              height={contentSize.height}
              listening={false}
              fill="#F8F8F8"
              cornerRadius={8}
            />
          )}
          {staticElements.map((ele) => (
            <SchemaBasicElement key={ele.id} element={ele} />
          ))}
        </Layer>
      )}

      {/* Слой столов: перерисовывается при изменении tablesSchedule */}
      {tableElements.length > 0 && (
        <Layer>
          {tableElements.map((ele) => (
            <SchemaTable
              key={ele.id}
              schemaTable={ele}
              selectedTableIds={selectedTableIds}
              onClick={onClickTable}
              tableSchedule={tablesSchedule[ele.table.id]}
              personImage={personImage}
              restaurantTz={restaurantTz}
              // isMobile={isMobile}
            />
          ))}
        </Layer>
      )}

      {selectedTables.length > 0 && (
        <Layer>
          <Rect
            x={0}
            y={0}
            width={contentSize.width}
            height={contentSize.height}
            listening={false}
            // fill="#14141E"
            // opacity={0.5}
            fill="rgba(0, 0, 0, 0.5)"
            cornerRadius={8}
          />

          {selectedTables.map((schemaTable) => (
            <SchemaTable
              key={schemaTable.id}
              schemaTable={schemaTable}
              selectedTableIds={selectedTableIds}
              onClick={onClickTable}
              tableSchedule={tablesSchedule[schemaTable.table.id]}
              personImage={personImage}
              // scale={scale}
              restaurantTz={restaurantTz}
              // isMobile={isMobile}
            />
          ))}
        </Layer>
      )}
    </Stage>
  );
});
